import React, { useEffect, useRef, useState } from 'react';
import { PreviewSectionHeader, useT, PreviewTabKey } from '../../preview';
import style from './style.module.scss';

type AcvtHomePromotionsProps = {
    couponCards: React.ReactNode;
    inStoreCards: React.ReactNode;
    promotedBanners?: React.ReactNode;
    viewAll: (tab: PreviewTabKey) => void;
};

type ScrollState = {
    canPrev: boolean;
    canNext: boolean;
};

/**
 * Tracks whether a horizontally scrollable element can scroll further
 * left or right, and exposes a smooth `scrollBy` helper for the
 * section header chevrons. Listens to scroll + size changes so the
 * disabled state stays in sync as the user drags or as content loads.
 */
const useHorizontalScrollControls = () => {
    const ref = useRef<HTMLDivElement>(null);
    const [state, setState] = useState<ScrollState>({
        canPrev: false,
        canNext: false,
    });

    useEffect(() => {
        const el = ref.current;
        if (!el) {
            return;
        }
        const update = () => {
            const maxScroll = el.scrollWidth - el.clientWidth;
            setState({
                canPrev: el.scrollLeft > 1,
                canNext: el.scrollLeft < maxScroll - 1,
            });
        };
        update();
        el.addEventListener('scroll', update, { passive: true });
        const resizeObserver =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(update)
                : null;
        resizeObserver?.observe(el);
        return () => {
            el.removeEventListener('scroll', update);
            resizeObserver?.disconnect();
        };
    }, []);

    const scrollBy = (delta: number) => {
        ref.current?.scrollBy({ left: delta, behavior: 'smooth' });
    };

    return { ref, ...state, scrollBy };
};

// Default scroll step roughly matches one card + the gap between cards
// so each chevron click advances by exactly one slot in the strip.
const SCROLL_STEP = 168;

/**
 * Home page promotions section. Refactored for CTCAN-117: the previous
 * `AcvtTopTabs` (Coupons / In-Store tabs) is replaced by two stacked
 * sections, each rendered with the new `PreviewSectionHeader` (title +
 * dark count pill) per the Figma design.
 */
export const AcvtHomePromotions = ({
    couponCards,
    inStoreCards,
    promotedBanners,
    viewAll,
}: AcvtHomePromotionsProps) => {
    const t = useT();
    const couponCount = React.Children.toArray(couponCards).length;
    const inStoreCount = React.Children.toArray(inStoreCards).length;

    const coupons = useHorizontalScrollControls();
    const inStore = useHorizontalScrollControls();

    return (
        <div className={style.promotions_content}>
            {promotedBanners && (
                <div className={style.promoted_banners_section}>
                    {promotedBanners}
                </div>
            )}
            <section className={style.promotions_section}>
                <PreviewSectionHeader
                    title={t.home.coupons}
                    count={couponCount}
                    onPrev={() => coupons.scrollBy(-SCROLL_STEP)}
                    onNext={() => coupons.scrollBy(SCROLL_STEP)}
                    prevDisabled={!coupons.canPrev}
                    nextDisabled={!coupons.canNext}
                />
                <div ref={coupons.ref} className={style.side_scroll}>
                    {couponCards}
                </div>
                <div className={style.separator} />
            </section>
            <section className={style.promotions_section}>
                <PreviewSectionHeader
                    title={t.home.inStore}
                    count={inStoreCount}
                    onPrev={() => inStore.scrollBy(-SCROLL_STEP)}
                    onNext={() => inStore.scrollBy(SCROLL_STEP)}
                    prevDisabled={!inStore.canPrev}
                    nextDisabled={!inStore.canNext}
                />
                <div ref={inStore.ref} className={style.side_scroll}>
                    {inStoreCards}
                </div>
                <div className={style.separator} />
            </section>
        </div>
    );
};
